import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/utils/query-keys';
import { useUpdateBeraReport } from '@/hooks/useUpdateBeraReport';
import { useDownloadBeraReportV2Pdf } from '@/hooks/useDownloadBeraReportV2Pdf';
import { useDebouncedComment } from '@/components/views/BeraJob/BeraReportV2/_hooks';
import { COMMENT_DEBOUNCE_MS, DEFAULT_LOCALE, EMPTY_TEXT } from '@/components/views/BeraJob/BeraReportV2/constants';

import type { BeraReportV2Result } from '@/types/Bera';

interface ResultActionsParams {
	organization_id: string;
	company_id: string;
	report_id: string;
	result?: BeraReportV2Result;
}

export function useResultActions({ organization_id, company_id, report_id, result }: ResultActionsParams) {
	const queryClient = useQueryClient();

	const [isConsolidated, setIsConsolidated] = useState<boolean>(false);

	const loadedResultFor = useRef<string | null>(null);
	const lastSavedComment = useRef<string>(EMPTY_TEXT);

	const { comment, setComment, handleCommentChange, handleCommentBlur } = useDebouncedComment({
		delay: COMMENT_DEBOUNCE_MS,
		onSave: saveComment
	});

	const { mutateAsync: updateBeraReport, mutate: updateScores, isLoading: isUpdating } = useUpdateBeraReport();
	const { mutateAsync: downloadPdf, isLoading: isDownloading } = useDownloadBeraReportV2Pdf();

	const baseParams = { id: report_id, organization_id, company_id };

	function invalidateReport(): void {
		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORT_V2]);
		queryClient.invalidateQueries([QUERY_KEYS.GET_BERA_REPORTS_V2]);
	}

	useEffect(() => {
		if (!result || loadedResultFor.current === result.id) {
			return;
		}

		loadedResultFor.current = result.id;
		lastSavedComment.current = result.comment;
		setComment(result.comment);
		setIsConsolidated(result.consolidated);

		const payload = {
			...baseParams,
			severity: result.scores.severity,
			exposure: result.scores.exposure,
			vulnerability: result.scores.vulnerability
		};

		updateScores(payload, { onSuccess: invalidateReport });
	}, [result]);

	async function saveComment(value: string): Promise<void> {
		if (value === lastSavedComment.current) {
			return;
		}

		lastSavedComment.current = value;

		const payload = { ...baseParams, comment: value || null };

		await updateBeraReport(payload);
	}

	async function handleToggleConsolidate(): Promise<void> {
		const isConsolidatedNow = !isConsolidated;
		const payload = { ...baseParams, consolidated: isConsolidatedNow };

		await updateBeraReport(payload);

		setIsConsolidated(isConsolidatedNow);
		invalidateReport();
		message.success(I18n.get('Report updated successfully'));
	}

	async function handleDownloadPdf(): Promise<void> {
		const params = { ...baseParams, locale: window.navigator.language ?? DEFAULT_LOCALE };

		await downloadPdf(params);
	}

	return {
		comment,
		isConsolidated,
		isUpdating,
		isDownloading,
		handleCommentChange,
		handleCommentBlur,
		handleToggleConsolidate,
		handleDownloadPdf
	};
}
